package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.CanvasAst
import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.MenuContentAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipDataBinding
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.MigrationConfidence
import top.wcpe.allincore.dsl.compiler.diag.MigrationHint
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.semantic.EventProps
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 解析 `root { element }` 与嵌套 `kind "id" { ... }` 结构。
 *
 * MVP 强制 root 块内有且仅有 1 个顶层元素。
 */
internal class ElementBlockParser(
    private val p: Parser,
    private val dslMode: DslMode = DslMode.LEGACY,
) {

    /** M6-b：支持 source/形参的容器 kind 白名单。 */
    private companion object {
        val M6B_CONTAINER_KINDS = setOf("column", "row", "box", "scrollview", "stack", "virtualList", "grid")
        val MENU_REF_PROPS = setOf("rightClickMenu", "contextMenu")
        val MENU_BLOCK_KINDS = setOf("rightClickMenu", "contextMenu")
        val MENU_PLACEMENTS = setOf("auto", "top", "bottom", "left", "right")
        val OVERLAY_ANIMATIONS = setOf("none", "fade", "scale", "fade-scale")
        val OVERLAY_PSEUDO_KINDS = setOf(
            "tooltip",
            "modal",
            "menu",
            "rightClickMenu",
            "contextMenu",
            "slotGrid",
            "slot",
            "inventory",
        )
        val OVERLAY_NESTED_KINDS = setOf(
            "tooltip",
            "modal",
            "menu",
            "rightClickMenu",
            "contextMenu",
        )
        val OVERLAY_FORBIDDEN_KINDS = setOf(
            "slotGrid",
            "slot",
            "inventory",
        )
    }

    fun parseRoot(): ElementAst {
        p.expect(TokenType.KW_ROOT)
        p.expect(TokenType.LBRACE, "root 块缺少 '{'")
        p.skipNewlines()

        if (p.check(TokenType.RBRACE)) p.fail("root 块内必须包含一个元素")
        val element = parseElement()
        p.skipNewlines()

        if (!p.check(TokenType.RBRACE)) {
            p.fail("root 块内必须恰好包含一个顶层元素")
        }
        p.expect(TokenType.RBRACE, "root 块缺少 '}'")
        return element
    }

    fun parseElement(allowOverlayPseudoElements: Boolean = false): ElementAst {
        // canvas 作为叶子元素与 box/text 并列：`canvas "id" { ... }`
        if (p.check(TokenType.KW_CANVAS)) {
            if (allowOverlayPseudoElements && p.peek(1).type == TokenType.LBRACE) {
                return parsePseudoOverlayElement("canvas", p.advance().position, allowOverlayPseudoElements)
            }
            return parseCanvasElement()
        }
        // M6-d：字符串字面量 kind（`"name" "id" { ... }`）按组件调用解析。
        if (p.check(TokenType.STRING)) {
            return parseStringKindElement()
        }
        val kindTok = p.expect(TokenType.IDENTIFIER, "元素必须以 kind 标识符开头")
        if (allowOverlayPseudoElements && p.check(TokenType.LBRACE)) {
            return parsePseudoOverlayElement(kindTok.lexeme, kindTok.position, allowOverlayPseudoElements)
        }
        val idTok = p.expect(TokenType.STRING, "元素 kind 后必须跟字符串 id")
        p.expect(TokenType.LBRACE, "元素 '${kindTok.lexeme}' 头部后缺少 '{'")
        p.skipNewlines()

        // M6-b：容器形参列表 `{ item -> ... }` / `{ k, v -> ... }`
        // 探测条件：当前为 IDENT，且其后（跳过 NEWLINE）首个非 NEWLINE token 为 COMMA / COLON / ARROW
        val sourceParams = tryParseSourceParams(kindTok.lexeme)

        // Phase B.4 修复：将 source 形参注册到当前块作用域，使形参可作为裸标识符被引用
        // （如 `item = entry`），并在元素体解析结束后弹出，保证形参不泄漏到外层。
        val props = mutableListOf<ElementAst.PropAssign>()
        val children = mutableListOf<ElementAst>()
        // 窗口化容器隐式注入模板可用的裸标识符，让子模板内可直接引用 ${index} 等。
        // - virtualList / virtualGrid / virtualMasonry / multiColumnList：仅 index
        // - virtualTree：index + 树节点行的派生字段（depth/isLeaf/isExpanded/nodeId/data）
        val implicitParams = when (kindTok.lexeme) {
            "virtualList", "virtualGrid", "virtualMasonry", "multiColumnList" -> listOf("index")
            "virtualTree" -> listOf("index", "depth", "isLeaf", "isExpanded", "nodeId", "nodeData")
            else -> emptyList()
        }
        // B1 上下文判据：与 ComponentExpanderPass.isCall 同款——kind 首字母大写视作组件调用，
        // 此时元素体内 `slot "name" { ... }` 才被识别为具名填充块。
        val isComponentCallContext = kindTok.lexeme.firstOrNull()?.isUpperCase() == true
        val bodyResult = withSourceParamScope(sourceParams, implicitParams) {
            parseElementBody(kindTok.lexeme, props, children, allowOverlayPseudoElements, isComponentCallContext)
        }

        val closeToken = p.expect(TokenType.RBRACE, "元素 '${kindTok.lexeme}' 缺少 '}'")
        val sourceExpr = validateSourceBindings(kindTok, props, sourceParams)
        return ElementAst(
            kind = kindTok.lexeme,
            id = idTok.literalValue as String,
            props = props,
            children = children,
            namedSlotContent = bodyResult.namedSlotContent,
            sourceParams = sourceParams,
            sourceExpr = sourceExpr,
            tooltipContent = bodyResult.tooltipContent,
            toastContent = bodyResult.toastContent,
            rightClickMenuContent = bodyResult.rightClickMenuContent,
            contextMenuContent = bodyResult.contextMenuContent,
            menuBlocks = bodyResult.menuBlocks,
            styleClasses = bodyResult.styleClasses,
            modifiers = bodyResult.modifiers,
            themeOverride = bodyResult.themeOverride,
            position = p.completePosition(kindTok.position, closeToken),
        )
    }

    /**
     * M6-d：解析字符串字面量 kind 形式的元素（`"name" "id" { ... }`）。
     *
     * 不参与容器形参解析，亦不走 source 强约束；其在下游被
     * [top.wcpe.allincore.dsl.compiler.pipeline.ComponentExpanderPass] 一律按组件调用展开。
     */
    private fun parseStringKindElement(): ElementAst {
        val kindTok = p.expect(TokenType.STRING, "元素必须以字符串 kind 开头")
        if (dslMode == DslMode.STRICT) {
            p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, kindTok.position, "字符串式元素 kind")
        }
        val idTok = p.expect(TokenType.STRING, "元素 kind 后必须跟字符串 id")
        val name = kindTok.literalValue as String
        p.expect(TokenType.LBRACE, "元素 '$name' 头部后缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        val children = mutableListOf<ElementAst>()
        // 字符串 kind 一律按组件调用处理（见 isStringKindCall），故启用具名填充块识别。
        val bodyResult = parseElementBody(name, props, children, isComponentCallContext = true)
        val closeToken = p.expect(TokenType.RBRACE, "元素 '$name' 缺少 '}'")
        return ElementAst(
            kind = name,
            id = idTok.literalValue as String,
            props = props,
            children = children,
            namedSlotContent = bodyResult.namedSlotContent,
            tooltipContent = bodyResult.tooltipContent,
            toastContent = bodyResult.toastContent,
            rightClickMenuContent = bodyResult.rightClickMenuContent,
            contextMenuContent = bodyResult.contextMenuContent,
            menuBlocks = bodyResult.menuBlocks,
            styleClasses = bodyResult.styleClasses,
            modifiers = bodyResult.modifiers,
            isStringKindCall = true,
            themeOverride = bodyResult.themeOverride,
            position = p.completePosition(kindTok.position, closeToken),
        )
    }

    /**
     * 解析元素体内部（`{` 与 `}` 之间）：属性赋值、子元素、on/effects/children 占位等。
     *
     * 调用前：`{` 已消费、形参（如有）已解析、newline 已跳过。
     * 返回时：游标停在结尾的 `}` 之前。
     */
    private fun parseElementBody(
        kindLexeme: String,
        props: MutableList<ElementAst.PropAssign>,
        children: MutableList<ElementAst>,
        allowOverlayPseudoElements: Boolean = false,
        isComponentCallContext: Boolean = false,
    ): ElementBodyResult {
        var tooltipContent: TooltipContentAst? = null
        var toastContent: TooltipContentAst? = null
        var rightClickMenuContent: TooltipContentAst.Component? = null
        var contextMenuContent: TooltipContentAst.Component? = null
        val menuBlocks = mutableListOf<MenuContentAst>()
        // 具名插槽填充内容：仅组件调用上下文（B1 判据）下收集 `slot "name" { ... }`，按名分组。
        val namedSlotContent = linkedMapOf<String, MutableList<ElementAst>>()
        // 编译期 style-class 样式层：累积本元素声明的 class 类名（按空白切分），不进入 props。
        val styleClasses = mutableListOf<String>()
        val modifiers = mutableListOf<ModifierAst>()
        var themeOverride: ThemeAst? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val head = p.current()
            val next = p.peek(1)

            // B1 具名填充块：仅在组件调用上下文（父 kind 首字母大写 / 字符串 kind）下，把
            // `slot "name" { ... }` 形态特判为具名插槽内容，归入 namedSlotContent[name]。
            // 非组件调用上下文 严禁误判：普通容器里的 `slot "x" {}` 仍按 overlay/slot 元素 kind
            // 走原解析路径（保持物品槽 / overlay 伪元素语义）。
            if (isComponentCallContext &&
                head.type == TokenType.IDENTIFIER && head.lexeme == "slot" &&
                next.type == TokenType.STRING && p.peek(2).type == TokenType.LBRACE
            ) {
                parseNamedSlotFillBlock(namedSlotContent, allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // overlay 子树中的伪 overlay 叶子：`tooltip { ... }` / `modal { ... }` / `slot { ... }`
            if (allowOverlayPseudoElements && head.type == TokenType.KW_CANVAS && next.type == TokenType.LBRACE) {
                children += parseElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            if (allowOverlayPseudoElements && head.type == TokenType.IDENTIFIER && next.type == TokenType.LBRACE && isOverlayPseudoKind(head.lexeme)) {
                children += parseElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }

            // RFC-RECOMPOSITION：元素树位置的 if/when 条件分支
            if (head.type == TokenType.KW_IF || head.type == TokenType.KW_WHEN) {
                children += parseConditionalElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // $for：元素树位置的 for 循环重组
            if (head.type == TokenType.KW_FOR) {
                children += parseForElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // toast 富内容：`content { ... }`
            if (kindLexeme == "toast" && head.type == TokenType.IDENTIFIER && head.lexeme == "content" && next.type == TokenType.LBRACE) {
                toastContent = parseToastInlineBlock(head.position)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // 旧 tooltip / 新 overlay tooltip 语法：`tooltip = "..."` / `tooltip { ... }` / `tooltip = TooltipCard { ... }`
            if (head.type == TokenType.IDENTIFIER && head.lexeme == "tooltip") {
                tooltipContent = parseTooltipStatement(props)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // 菜单属性：`rightClickMenu { ... }` / `contextMenu { ... }`（小写属性形式，任意布局）
            if (head.type == TokenType.IDENTIFIER && head.lexeme in MENU_BLOCK_KINDS && next.type == TokenType.LBRACE) {
                menuBlocks += parseMenuBlock(head.lexeme)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // canvas 子元素：`canvas "id" { ... }` —— 与 box/text 并列的叶子元素
            if (head.type == TokenType.KW_CANVAS) {
                children += parseElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }

            // 优先处理 children 占位关键字（匿名 `children` 与具名 `children "header"`）
            if (head.type == TokenType.KW_CHILDREN) {
                if (!p.inComponentFile) {
                    p.fail(DslError.E305_CHILDREN_MISUSE, head.position, "children 关键字仅可出现在 component 文件内")
                }
                val childTok = p.advance()
                // 可选具名洞名：`children "header"` → slotName = "header"；裸 `children` → slotName = null（匿名洞）。
                var slotName: String? = null
                if (p.check(TokenType.STRING)) {
                    slotName = p.advance().literalValue as String
                }
                // 不论匿名还是具名，占位本身都不允许再带属性 / 子块（保留原 E305 约束）。
                val childNext = p.peek(0)
                if (childNext.type == TokenType.LBRACE || childNext.type == TokenType.STRING || childNext.type == TokenType.EQUALS) {
                    p.fail(DslError.E305_CHILDREN_MISUSE, head.position, "children 占位不允许带 id / 属性 / 子块")
                }
                children += ElementAst(
                    kind = "",
                    id = "",
                    props = emptyList(),
                    children = emptyList(),
                    isChildrenSlot = true,
                    slotName = slotName,
                    position = childTok.position,
                )

                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            // M6-d：字符串字面量 kind 的嵌套子元素（`"name" "id" { ... }`）
            if (head.type == TokenType.STRING) {
                children += parseElement(allowOverlayPseudoElements)
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }

            if (!isMemberHead(head.type)) {
                p.fail("元素体内期望属性名或子元素 kind，实际为 '${head.lexeme}'")
            }

            // `on { trigger = "animName" ... }` 触发器块：等价于多条 PropAssign(triggerName, StringLit)
            if (head.type == TokenType.IDENTIFIER && head.lexeme == "on" && next.type == TokenType.LBRACE) {
                parseOnBlock(props)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            // `effects { ... }` / `modifiers { ... }` 装饰器块：解析为有序视觉 modifier，不进入普通 props。
            // effects 是旧入口，modifiers 是 ADR-0041 的新入口；两者统一输出 v32 modifier 链。
            if (head.type == TokenType.IDENTIFIER && isEffectBlockName(head.lexeme) && next.type == TokenType.LBRACE) {
                modifiers += parseEffectsBlock(head.lexeme)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            if (head.type == TokenType.IDENTIFIER && head.lexeme == "themeOverride" && next.type == TokenType.LBRACE) {
                if (dslMode == DslMode.LEGACY) {
                    p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, head.position, "themeOverride")
                }
                if (themeOverride != null) p.fail("themeOverride 块只能声明一次", head.position)
                p.advance()
                themeOverride = ThemeBlockParser(p, dslMode).parseBodyOnly("themeOverride")
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            if (head.type == TokenType.IDENTIFIER && isDomainBlockName(head.lexeme) && next.type == TokenType.LBRACE) {
                if (dslMode == DslMode.LEGACY) {
                    p.fail(DslError.AUI_V2_LEGACY_FORBIDS_V2_SYNTAX, head.position, head.lexeme)
                }
                props += parseDomainBlock(head.lexeme)
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            // `mask { type = "pie" startAngle = ... sweepAngle = ... param1/2/3 = ... }`
            // 元素体内的语义化 mask 子块，参考 effects {} 风格。
            // 字段语义：
            //   type        → 展平为 prop "mask"（字符串字面量，由 VisualPropContributor 路由 MaskType）
            //   startAngle  → 展平为 prop "mask.pie.startAngle"
            //   sweepAngle  → 展平为 prop "mask.pie.sweepAngle"
            //   param1/2/3  → 展平为 prop "maskParam1/2/3"
            // 块内字段间允许 NEWLINE 或 ';' 任一作为分隔。
            if (head.type == TokenType.IDENTIFIER && head.lexeme == "mask" && next.type == TokenType.LBRACE) {
                modifiers += parseMaskBlock()
                if (!p.check(TokenType.RBRACE)) {
                    p.expect(TokenType.NEWLINE, "元素成员后缺少换行")
                    p.skipNewlines()
                }
                continue
            }
            // RFC-INPUT-EVENT-UNIFICATION §5.1 形式 2：事件 prop 块形式
            // `onClick { event -> ... }` —— 等价于等号形式 `onClick = { event -> ... }`。
            // 仅对 EventProps.NAMES 白名单启用，避免与子元素 `kind "id" { ... }` 等其他
            // 块语法冲突；普通元素内非事件 prop 仍要求 `=`，overlay inline 子树内可作为无 id 子元素解析。
            if (head.type == TokenType.IDENTIFIER && next.type == TokenType.LBRACE) {
                if (head.lexeme in EventProps.NAMES) {
                    parseEventPropBlockForm(head, props)
                } else if (allowOverlayPseudoElements) {
                    children += parseElement(allowOverlayPseudoElements)
                } else {
                    p.fail(
                        "元素 prop '${head.lexeme}' 不支持块形式 '{ ... }'；" +
                            "块形式仅支持事件 prop（如 onClick / onChange），非事件 prop 请使用 '=' 赋值",
                        head.position,
                    )
                }
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
                continue
            }
            when (next.type) {
                TokenType.EQUALS -> {
                    // 属性赋值
                    val nameTok = p.advance()
                    p.advance() // '='
                    // RFC-INPUT-EVENT-UNIFICATION Stage 2：prop 表达式需要支持 lambda 字面量
                    // （事件 prop `onClick = { event -> ... }`）。复用 ActionBlockParser 的
                    // ExpressionParser 拿到 lambdaBodyParser 钩子；allowBareIdent=false，
                    // 即非事件 prop 仍走原有解析约束。
                    val expr = if (dslMode == DslMode.STRICT && isStaticSymbolProp(nameTok.lexeme)) {
                        parseStaticSymbolExpr(nameTok.lexeme)
                    } else {
                        ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
                    }
                    // 编译期 style-class 样式层：`class = "danger primary"` 特殊处理——
                    // 解析其字符串字面量值，按空白切分写入 styleClasses，绝不进入 props
                    // （避免进入 IR / prop 校验）。非字符串字面量值直接报错。
                    if (nameTok.lexeme == "class" || nameTok.lexeme == "use") {
                        if (dslMode == DslMode.STRICT) {
                            p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, nameTok.position, nameTok.lexeme)
                        }
                        warnLegacy(
                            nameTok.lexeme,
                            "modifiers { use <modifierBundle> }",
                            MigrationConfidence.MANUAL,
                            nameTok.position,
                        )
                        val literal = expr as? ExprAst.Literal.StringLit
                            ?: p.fail("${nameTok.lexeme} 属性值必须是字符串字面量（空格分隔的类名）", nameTok.position)
                        styleClasses += literal.value.split(Regex("\\s+")).filter { it.isNotEmpty() }
                    } else {
                        val prop = ElementAst.PropAssign(
                            name = nameTok.lexeme,
                            value = expr,
                            position = nameTok.position,
                        )
                        if (isLegacyTextStyleProp(kindLexeme, prop.name)) {
                            if (dslMode == DslMode.STRICT) {
                                p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, nameTok.position, prop.name)
                            }
                            warnLegacy(
                                prop.name,
                                "textStyle / textOverrides",
                                MigrationConfidence.SUGGESTED,
                                nameTok.position,
                            )
                        }
                        val modifier = modifierFromStyleProp(prop)
                        if (modifier != null) {
                            if (dslMode == DslMode.STRICT) {
                                p.fail(DslError.AUI_V2_STRICT_FORBIDS_LEGACY_SYNTAX, nameTok.position, nameTok.lexeme)
                            }
                            warnLegacy(
                                nameTok.lexeme,
                                "modifiers { surface { ${nameTok.lexeme} = ... } }",
                                MigrationConfidence.SUGGESTED,
                                nameTok.position,
                            )
                            modifiers += modifier
                        } else {
                            props += prop
                        }
                    }
                }
                TokenType.STRING -> {
                    if (head.type != TokenType.IDENTIFIER) {
                        p.fail("子元素 kind 必须是普通标识符，实际为 '${head.lexeme}'")
                    }
                    // 嵌套子元素
                    children += parseElement(allowOverlayPseudoElements)
                }
                else -> {
                    p.fail(
                        "元素成员不识别：'${head.lexeme}' 之后应为 '=' 或字符串 id，实际为 '${next.lexeme}'",
                    )
                }
            }

            if (!p.check(TokenType.RBRACE)) {
                expectMemberSeparator()
            }
        }
        if (rightClickMenuContent != null && contextMenuContent != null && props.none { it.name == "contextMenuTriggerMs" }) {
            p.fail(DslError.E738_MENU_TRIGGER_CONFLICT, contextMenuContent.position)
        }
        return ElementBodyResult(
            tooltipContent = tooltipContent,
            toastContent = toastContent,
            rightClickMenuContent = rightClickMenuContent,
            contextMenuContent = contextMenuContent,
            menuBlocks = menuBlocks,
            styleClasses = styleClasses,
            modifiers = modifiers,
            themeOverride = themeOverride,
            namedSlotContent = namedSlotContent.mapValues { it.value.toList() },
        )
    }

    private data class ElementBodyResult(
        val tooltipContent: TooltipContentAst?,
        val toastContent: TooltipContentAst?,
        val rightClickMenuContent: TooltipContentAst.Component?,
        val contextMenuContent: TooltipContentAst.Component?,
        val menuBlocks: List<MenuContentAst>,
        /** 编译期 style-class 样式层：本元素声明的 class 类名（已按空白切分）。 */
        val styleClasses: List<String> = emptyList(),
        /** FR-29：本元素显式声明的视觉 modifier。 */
        val modifiers: List<ModifierAst> = emptyList(),
        val themeOverride: ThemeAst? = null,
        /** 具名插槽填充内容：键为插槽名，值为该名下子元素列表；仅组件调用上下文非空。 */
        val namedSlotContent: Map<String, List<ElementAst>> = emptyMap(),
    )

    /**
     * 解析组件调用方的具名填充块 `slot "name" { ... }`，把块内子元素归入 [namedSlotContent]。
     *
     * 调用前提：当前 token 为 IDENTIFIER("slot")，peek(1) 为 STRING，peek(2) 为 LBRACE，
     * 且已确认处于组件调用上下文（B1 判据）。块内子元素与普通元素体的子元素解析一致
     * （递归 [parseElementBody]，复用 children/控制流/伪 overlay 等全部逻辑）。
     */
    private fun parseNamedSlotFillBlock(
        namedSlotContent: MutableMap<String, MutableList<ElementAst>>,
        allowOverlayPseudoElements: Boolean,
    ) {
        p.advance() // 消费 "slot"
        val name = p.advance().literalValue as String // 消费插槽名 STRING
        p.expect(TokenType.LBRACE, "具名插槽 '$name' 头部后缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        val children = mutableListOf<ElementAst>()
        // 具名填充块本身不承载 props / overlay 子内容，仅收集子元素；此处复用 parseElementBody
        // 解析子元素（其 props/overlay 结果丢弃——填充块层级不消费这些，子元素自身的仍保留）。
        parseElementBody("slot", props, children, allowOverlayPseudoElements)
        p.expect(TokenType.RBRACE, "具名插槽 '$name' 缺少 '}'")
        // 同名填充块允许多次出现时合并（按解析顺序追加）；重复声明侧的校验交给展开阶段，
        // 这里仅做内容收集。
        namedSlotContent.getOrPut(name) { mutableListOf() }.addAll(children)
    }

    private fun parseToastInlineBlock(position: SourcePosition): TooltipContentAst.Inline {
        p.expect(TokenType.IDENTIFIER, "content 块缺少关键字")
        p.expect(TokenType.LBRACE, "content 块缺少 '{'")
        p.skipNewlines()
        if (p.check(TokenType.RBRACE)) {
            p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, position, "content", "toast")
        }
        val root = parseElement(true)
        p.skipNewlines()
        if (!p.check(TokenType.RBRACE)) {
            p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, position, "rootCount", "toast")
        }
        val close = p.expect(TokenType.RBRACE, "content 块缺少 '}'")
        validateToastInlineTree(root)
        return TooltipContentAst.Inline(root, p.completePosition(position, close))
    }

    private fun parseToastComponentStatement(position: SourcePosition): TooltipContentAst.Component {
        p.expect(TokenType.IDENTIFIER, "toast 组件内容缺少关键字")
        p.expect(TokenType.EQUALS, "toast 组件内容后期望 '='")
        if (!p.check(TokenType.IDENTIFIER) || p.peek(1).type != TokenType.LBRACE) {
            p.fail(DslError.E742_TOAST_COMPONENT_INVALID, position, p.current().lexeme)
        }
        val component = parseComponentRef("toast", position)
        if (!isPascalCase(component.name)) {
            p.fail(DslError.E742_TOAST_COMPONENT_INVALID, position, component.name)
        }
        return component
    }

    private fun parseTooltipStatement(
        props: MutableList<ElementAst.PropAssign>,
    ): TooltipContentAst? {
        val tooltipTok = p.advance()
        return when {
            p.check(TokenType.LBRACE) -> parseTooltipInlineBlock(tooltipTok.position)
            p.check(TokenType.EQUALS) -> {
                p.advance()
                val expr = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
                props += ElementAst.PropAssign(
                    name = "tooltip",
                    value = expr,
                    position = tooltipTok.position,
                )
                if (isTextExpression(expr)) {
                    TooltipContentAst.Text(expr, tooltipTok.position.endingAt(expr.range.end))
                } else {
                    null
                }
            }
            else -> p.fail("tooltip 后期望 '{' 或 '='", tooltipTok.position)
        }
    }

    private fun parseMenuComponentStatement(menuProp: String): TooltipContentAst.Component {
        val menuTok = p.advance()
        p.expect(TokenType.EQUALS, "$menuProp 后期望 '='")
        if (!p.check(TokenType.IDENTIFIER) || p.peek(1).type != TokenType.LBRACE) {
            p.fail(DslError.E735_OVERLAY_COMPONENT_UNREGISTERED, menuTok.position, p.current().lexeme)
        }
        val component = parseComponentRef(menuProp, menuTok.position)
        if (!isPascalCase(component.name)) {
            p.fail(DslError.E735_OVERLAY_COMPONENT_UNREGISTERED, menuTok.position, component.name)
        }
        return component
    }

    private fun parseMenuBlock(name: String): MenuContentAst {
        val menuTok = p.advance()
        p.expect(TokenType.LBRACE, "$name 块缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        // 先解析顶部的 overlay 配置 prop（placement/animation/fadeIn/fadeOut/scaleIn/background）
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val head = p.current()
            val next = p.peek(1)
            // 只有 IDENT = expr 形式才是配置 prop
            if (isMemberHead(head.type) && next.type == TokenType.EQUALS) {
                props += parseSimpleProp()
                validateOverlayConfig(props.last())
                if (!p.check(TokenType.RBRACE)) {
                    expectMemberSeparator()
                }
            } else {
                break
            }
        }
        // 剩余内容按普通元素体解析（支持任意 DSL 布局）
        if (p.check(TokenType.RBRACE)) {
            p.fail("$name 块内必须包含至少一个元素", menuTok.position)
        }
        val root = parseElement(false)
        p.skipNewlines()
        val close = p.expect(TokenType.RBRACE, "$name 块缺少 '}'")
        val kind = if (name == "rightClickMenu") {
            MenuContentAst.Kind.RIGHT_CLICK_MENU
        } else {
            MenuContentAst.Kind.CONTEXT_MENU
        }
        return MenuContentAst(kind, props, root, p.completePosition(menuTok.position, close))
    }

    private fun parseMenuChild(kind: String): ElementAst {
        val kindTok = p.advance()
        p.expect(TokenType.LBRACE, "$kind 块缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            props += parseSimpleProp()
            if (!p.check(TokenType.RBRACE)) {
                expectMemberSeparator()
            }
        }
        val close = p.expect(TokenType.RBRACE, "$kind 块缺少 '}'")
        if (kind == "item") {
            validateMenuItem(props, kindTok.position)
        }
        return ElementAst(
            kind = kind,
            id = "",
            props = props,
            children = emptyList(),
            position = p.completePosition(kindTok.position, close),
        )
    }

    private fun parseSimpleProp(): ElementAst.PropAssign {
        val propTok = p.current()
        if (!isMemberHead(propTok.type)) {
            p.expect(TokenType.IDENTIFIER, "期望 prop 名")
        }
        p.advance()
        p.expect(TokenType.EQUALS, "prop '${propTok.lexeme}' 后期望 '='")
        val expr = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
        return ElementAst.PropAssign(propTok.lexeme, expr, propTok.position)
    }

    private fun validateMenuItem(props: List<ElementAst.PropAssign>, position: SourcePosition) {
        if (props.none { it.name == "text" } || props.none { it.name == "onClick" }) {
            p.fail(DslError.E737_MENU_ITEM_REQUIRES_TEXT_OR_ON_CLICK, position)
        }
    }

    private fun validateOverlayConfig(prop: ElementAst.PropAssign) {
        when (prop.name) {
            "placement" -> {
                val value = prop.value as? ExprAst.Literal.StringLit
                    ?: p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, prop.value)
                if (value.value !in MENU_PLACEMENTS) {
                    p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, value.value)
                }
            }
            "animation" -> {
                val value = prop.value as? ExprAst.Literal.StringLit
                    ?: p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, prop.value)
                if (value.value !in OVERLAY_ANIMATIONS) {
                    p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, value.value)
                }
            }
            "fadeIn", "fadeOut" -> {
                val value = (prop.value as? ExprAst.Literal.NumberLit)?.value?.toDouble()
                if (value == null || value < 0.0) {
                    p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, prop.value)
                }
            }
            "scaleIn" -> {
                if (prop.value !is ExprAst.Literal.BoolLit) {
                    p.fail(DslError.E736_OVERLAY_PLACEMENT_OR_ANIMATION_INVALID, prop.position, prop.name, prop.value)
                }
            }
        }
    }

    private fun parseTooltipInlineBlock(
        position: SourcePosition,
    ): TooltipContentAst.Inline {
        p.expect(TokenType.LBRACE, "tooltip 块缺少 '{'")
        p.skipNewlines()
        // ADR-0036：先解析块顶部的 data 句柄声明（0..N），再解析唯一根元素。
        val dataBindings = mutableListOf<TooltipDataBinding>()
        val seenNames = mutableSetOf<String>()
        while (p.check(TokenType.KW_DATA)) {
            val binding = parseTooltipDataBinding()
            if (!seenNames.add(binding.name)) {
                p.fail(DslError.E786_TOOLTIP_DATA_DUPLICATE, binding.position, binding.name)
            }
            dataBindings += binding
            p.skipNewlines()
        }
        if (p.check(TokenType.RBRACE)) {
            p.fail(DslError.E732_OVERLAY_SUBTREE_ROOT_COUNT_MISMATCH, position)
        }
        // data 句柄名注册为 source 形参作用域：body 内 `setInfo.ready` 被解析为 IdentRef（运行时走 locals），
        // 而非未声明标识符或 state 引用——与容器 source 形参完全同一套机制。
        p.pushSourceParamScope()
        val root = try {
            for (b in dataBindings) p.declareSourceParam(b.name)
            // RFC-RECOMPOSITION：tooltip 块根可以是 if/when 控制流，编译为 conditional 包装节点
            if (p.check(TokenType.KW_IF) || p.check(TokenType.KW_WHEN)) {
                parseConditionalElement(allowOverlayPseudoElements = true)
            } else {
                parseElement(allowOverlayPseudoElements = true)
            }
        } finally {
            p.popSourceParamScope()
        }
        p.skipNewlines()
        if (!p.check(TokenType.RBRACE)) {
            p.fail(DslError.E732_OVERLAY_SUBTREE_ROOT_COUNT_MISMATCH, position)
        }
        val close = p.expect(TokenType.RBRACE, "tooltip 块缺少 '}'")
        validateOverlayInlineTree(root)
        return TooltipContentAst.Inline(root, p.completePosition(position, close), dataBindings)
    }

    /**
     * ADR-0036：解析 tooltip 块内 `data NAME = net.tooltip("ns.handler", #{...})` 单条数据请求绑定。
     *
     * channel 必须是含 '.' 的字符串字面量；payload 必须是 `#{...}` Map 字面量，且其值表达式不得读
     * screen.state（沿用 overlay 子树 E730 约束，可读 hover 上下文 / slot.read）。
     */
    private fun parseTooltipDataBinding(): TooltipDataBinding {
        val dataTok = p.expect(TokenType.KW_DATA, "tooltip data 声明缺少 'data'")
        val nameTok = p.expect(TokenType.IDENTIFIER, "data 后缺少句柄名")
        p.expect(TokenType.EQUALS, "data 句柄名后缺少 '='")
        // net.tooltip(...)
        val netTok = p.expect(TokenType.IDENTIFIER, "data 句柄右侧必须为 net.tooltip(...)")
        if (netTok.lexeme != "net") {
            p.fail("data 句柄右侧必须为 net.tooltip(...)", netTok.position)
        }
        p.expect(TokenType.DOT, "net 后缺少 '.'")
        val tooltipTok = p.expect(TokenType.IDENTIFIER, "net. 后缺少 'tooltip'")
        if (tooltipTok.lexeme != "tooltip") {
            p.fail("net. 后缺少 'tooltip'", tooltipTok.position)
        }
        p.expect(TokenType.LPAREN, "net.tooltip 缺少 '('")
        p.skipNewlines()
        // 1) channel 字面量 "ns.handler"
        val channelExpr = ExpressionParser(p).parse()
        val channel = (channelExpr as? ExprAst.Literal.StringLit)?.value
            ?: p.fail("net.tooltip 第一个参数必须是字符串字面量 \"ns.handler\"", channelExpr.position)
        val dotIdx = channel.indexOf('.')
        if (dotIdx <= 0 || dotIdx >= channel.length - 1) {
            p.fail(DslError.E787_TOOLTIP_DATA_CHANNEL_INVALID, channelExpr.position, channel)
        }
        val namespace = channel.substring(0, dotIdx)
        val handler = channel.substring(dotIdx + 1)
        p.skipNewlines()
        p.expect(TokenType.COMMA, "net.tooltip channel 后缺少 ','")
        p.skipNewlines()
        // 2) payload Map 字面量 #{...}；强制 # 前缀（与 net.request 一致）
        if (p.check(TokenType.LBRACE)) {
            p.fail(DslError.E747_MAP_LITERAL_REQUIRES_HASH_PREFIX, p.current().position)
        }
        val payloadExpr = ExpressionParser(p).parse()
        val mapLit = payloadExpr as? ExprAst.MapLiteralExpr
            ?: p.fail("net.tooltip 第二个参数必须是 Map 字面量 #{\"k\": v}", payloadExpr.position)
        // payload 值表达式禁读 screen.state（E730）
        for (entry in mapLit.entries) {
            if (containsStateRef(entry.value)) {
                p.fail(DslError.E730_OVERLAY_SUBTREE_FORBIDS_SCREEN_STATE, entry.position, nameTok.lexeme)
            }
        }
        val payload = mapLit.entries.map { e ->
            ExprAst.NetPayloadEntry(key = e.key, value = e.value, position = e.position)
        }
        p.skipNewlines()
        p.expect(TokenType.RPAREN, "net.tooltip 缺少 ')'")
        // ADR-0036：可选 `when <expr>` 条件门控——为假时挂载期跳过请求；condition 同 payload 约束禁读 screen.state。
        var condition: ExprAst? = null
        if (p.check(TokenType.KW_WHEN)) {
            p.advance()
            p.skipNewlines()
            condition = ExpressionParser(p).parse()
            if (containsStateRef(condition)) {
                p.fail(DslError.E730_OVERLAY_SUBTREE_FORBIDS_SCREEN_STATE, condition.position, nameTok.lexeme)
            }
        }
        return TooltipDataBinding(
            name = nameTok.lexeme,
            namespace = namespace,
            handler = handler,
            payload = payload,
            position = dataTok.position,
            condition = condition,
        )
    }

    private fun parseTooltipComponentRef(position: SourcePosition): TooltipContentAst.Component =
        parseComponentRef("tooltip", position)

    private fun parseComponentRef(label: String, position: SourcePosition): TooltipContentAst.Component {
        val componentTok = p.expect(TokenType.IDENTIFIER, "$label 组件名缺失")
        p.expect(TokenType.LBRACE, "$label 组件 '${componentTok.lexeme}' 后缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            props += parseSimpleProp()
            if (!p.check(TokenType.RBRACE)) {
                expectMemberSeparator()
            }
        }
        val close = p.expect(TokenType.RBRACE, "$label 组件引用缺少 '}'")
        return TooltipContentAst.Component(componentTok.lexeme, props, p.completePosition(position, close))
    }

    private fun isPascalCase(name: String): Boolean = name.firstOrNull()?.isUpperCase() == true

    private fun parsePseudoOverlayElement(
        kind: String,
        position: SourcePosition,
        allowOverlayPseudoElements: Boolean,
    ): ElementAst {
        p.expect(TokenType.LBRACE, "元素 '$kind' 头部后缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        val children = mutableListOf<ElementAst>()
        val bodyResult = parseElementBody(kind, props, children, allowOverlayPseudoElements)
        val close = p.expect(TokenType.RBRACE, "元素 '$kind' 缺少 '}'")
        return ElementAst(
            kind = kind,
            id = "",
            props = props,
            children = children,
            tooltipContent = bodyResult.tooltipContent,
            toastContent = bodyResult.toastContent,
            rightClickMenuContent = bodyResult.rightClickMenuContent,
            contextMenuContent = bodyResult.contextMenuContent,
            menuBlocks = bodyResult.menuBlocks,
            styleClasses = bodyResult.styleClasses,
            position = p.completePosition(position, close),
        )
    }

    private fun validateOverlayInlineTree(root: ElementAst) {
        validateOverlayInlineNode(root)
    }

    private fun validateToastInlineTree(root: ElementAst) {
        validateToastInlineNode(root)
    }

    private fun validateToastInlineNode(element: ElementAst) {
        // $for 包装节点本身不参与 toast 内容约束校验，仅递归循环体。
        if (element.forContent != null) {
            validateToastInlineNode(element.forContent.body)
            return
        }
        if (element.tooltipContent != null || element.toastContent != null || element.rightClickMenuContent != null || element.contextMenuContent != null || element.menuBlocks.isNotEmpty()) {
            p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, element.position, "overlay", "toast")
        }
        if (element.kind in OVERLAY_NESTED_KINDS) {
            p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, element.position, element.kind, "toast")
        }
        if (element.kind in OVERLAY_FORBIDDEN_KINDS || element.kind == "canvas") {
            p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, element.position, element.kind, "toast")
        }
        for (prop in element.props) {
            if (prop.name == "tooltip") {
                p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, prop.position, prop.name, "toast")
            }
            if (prop.name in EventProps.NAMES) {
                p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, prop.position, prop.name, "toast")
            }
            if (containsStateRef(prop.value)) {
                p.fail(DslError.E741_TOAST_CONTENT_CONSTRAINT_VIOLATION, prop.position, prop.name, "toast")
            }
        }
        element.children.forEach { validateToastInlineNode(it) }
    }

    private fun validateOverlayInlineNode(element: ElementAst) {
        // RFC-RECOMPOSITION：conditional 包装节点本身不参与 kind / props 校验，仅递归 branch body。
        //
        // 设计决策（显式）：conditional 的 subject 与 branch.condition 表达式 *允许* 引用 screen state /
        // slot.read / 其他响应式来源——这正是元素树位置 if/when 重组的核心目的（每次 state 变化触发
        // selector 重算 → 旧分支整棵 dispose + 新分支 mount）。因此这里 *不对* subject / condition 应用
        // E730（OVERLAY_SUBTREE_FORBIDS_SCREEN_STATE）约束。
        //
        // branch body 内部仍然要走 validateOverlayInlineNode，意味着分支体内的 element prop 表达式
        // 仍然被 E730 / E734 等约束覆盖（与现有 overlay 子树语义一致）。
        if (element.conditionalContent != null) {
            element.conditionalContent.branches.forEach { validateOverlayInlineNode(it.body) }
            element.conditionalContent.elseBranch?.let { validateOverlayInlineNode(it) }
            return
        }
        // $for 包装节点同 conditional：本身不参与 kind/props 校验，仅递归循环体；
        // iterable 允许引用 screen state（与 $cond subject 一样豁免 E730）。
        if (element.forContent != null) {
            validateOverlayInlineNode(element.forContent.body)
            return
        }
        if (element.tooltipContent != null || element.rightClickMenuContent != null || element.contextMenuContent != null || element.menuBlocks.isNotEmpty()) {
            p.fail(DslError.E731_OVERLAY_SUBTREE_NESTED_OVERLAY, element.position, "overlay")
        }
        if (element.kind in OVERLAY_NESTED_KINDS) {
            p.fail(DslError.E731_OVERLAY_SUBTREE_NESTED_OVERLAY, element.position, element.kind)
        }
        if (element.kind in OVERLAY_FORBIDDEN_KINDS || element.kind == "canvas") {
            p.fail(DslError.E733_OVERLAY_SUBTREE_FORBIDDEN_ELEMENT, element.position, element.kind)
        }
        for (prop in element.props) {
            if (prop.name == "tooltip") {
                p.fail(DslError.E731_OVERLAY_SUBTREE_NESTED_OVERLAY, prop.position, prop.name)
            }
            if (prop.name in EventProps.NAMES) {
                p.fail(DslError.E734_OVERLAY_SUBTREE_EVENT_PROP_REQUIRES_INTERACTIVE, prop.position, prop.name)
            }
            if (containsStateRef(prop.value)) {
                p.fail(DslError.E730_OVERLAY_SUBTREE_FORBIDS_SCREEN_STATE, prop.position, prop.name)
            }
        }
        element.children.forEach { validateOverlayInlineNode(it) }
    }

    private fun isOverlayPseudoKind(kind: String): Boolean = kind in OVERLAY_PSEUDO_KINDS

    private fun isTextExpression(expr: ExprAst): Boolean =
        expr is ExprAst.Literal.StringLit || expr is ExprAst.Interp

    private fun containsStateRef(expr: ExprAst): Boolean = when (expr) {
        is ExprAst.StateRef -> true
        // session / persist 同样属于"反应式状态引用"，对 if/visible/text 等 prop 等价于 state。
        is ExprAst.SessionRef -> true
        is ExprAst.PersistRef -> true
        // derived 派生值是只读响应式引用，对 prop 等价于 state。
        is ExprAst.DerivedRef -> true
        is ExprAst.BinaryOp -> containsStateRef(expr.left) || containsStateRef(expr.right)
        is ExprAst.UnaryOp -> containsStateRef(expr.operand)
        is ExprAst.MemberAccess -> containsStateRef(expr.target)
        is ExprAst.SafeMemberAccess -> containsStateRef(expr.target)
        // M10 Stage 6：`target!!` 透传 target
        is ExprAst.NonNullAssert -> containsStateRef(expr.target)
        is ExprAst.MemberCall -> containsStateRef(expr.target) || expr.args.any(::containsStateRef)
                || expr.namedArgs.any { containsStateRef(it.value) }
        is ExprAst.SafeMemberCall -> containsStateRef(expr.target) || expr.args.any(::containsStateRef)
        is ExprAst.Elvis -> containsStateRef(expr.left) || containsStateRef(expr.right)
        is ExprAst.Ternary -> containsStateRef(expr.cond) || containsStateRef(expr.thenExpr) || containsStateRef(expr.elseExpr)
        is ExprAst.Interp -> expr.parts.any { part -> part is ExprAst.Interp.InterpPart.ExprPart && containsStateRef(part.expr) }
        is ExprAst.ListLiteralExpr -> expr.elements.any(::containsStateRef)
        is ExprAst.MapLiteralExpr -> expr.entries.any { containsStateRef(it.value) }
        is ExprAst.RangeExpr -> containsStateRef(expr.start) || containsStateRef(expr.endValue)
        is ExprAst.IndexExpr -> containsStateRef(expr.target) || containsStateRef(expr.index)
        is ExprAst.CallExpr -> containsStateRef(expr.callee) || expr.args.any(::containsStateRef)
        is ExprAst.FunRefExpr -> expr.boundArgs.any(::containsStateRef) || expr.boundNamedArgs.any { containsStateRef(it.value) }
        is ExprAst.BuiltinCallExpr -> expr.args.any(::containsStateRef)
        is ExprAst.NetRequestCall ->
            expr.payload.any { containsStateRef(it.value) } ||
                expr.callback?.let(::containsStateRef) == true ||
                expr.timeoutMs?.let(::containsStateRef) == true
        is ExprAst.NetSubscribeCall -> containsStateRef(expr.handler)
        is ExprAst.AwaitExpr ->
            expr.netRequestCall?.let(::containsStateRef) == true ||
                expr.suspendCall?.let(::containsStateRef) == true ||
                expr.tailExpr?.let(::containsStateRef) == true
        is ExprAst.IfExpr ->
            containsStateRef(expr.cond) ||
                containsStateRefStmts(expr.thenBody) ||
                containsStateRefStmts(expr.elseBody)
        is ExprAst.WhenExpr -> {
            containsStateRef(expr.subject) ||
                expr.branches.any { branch -> containsStateRefs(branch.values) || containsStateRefStmts(branch.body) } ||
                (expr.elseBody?.let(::containsStateRefStmts) == true)
        }

        is ExprAst.LambdaExpr -> false
        is ExprAst.PropsRef,
        is ExprAst.IdentRef,
        is ExprAst.LocalRef,
        is ExprAst.ThemeRef,
        is ExprAst.NullLit,
        is ExprAst.Literal.StringLit,
        is ExprAst.Literal.NumberLit,
        is ExprAst.Literal.BoolLit,
        is ExprAst.IsExpr -> false
    }

    private fun containsStateRefs(exprs: List<ExprAst>): Boolean = exprs.any(::containsStateRef)

    private fun containsStateRefStmts(stmts: List<StmtAst>): Boolean = stmts.any(::containsStateRef)

    private fun containsStateRef(stmt: StmtAst): Boolean = when (stmt) {
        is StmtAst.AssignStmt -> containsStateRef(stmt.rhs)
        is StmtAst.ExprStmt -> containsStateRef(stmt.call)
        is StmtAst.VarDeclStmt -> containsStateRef(stmt.initializer)
        is StmtAst.IfStmt ->
            containsStateRef(stmt.cond) ||
                containsStateRefStmts(stmt.thenBody) ||
                containsStateRefStmts(stmt.elseBody)
        is StmtAst.WhileStmt -> containsStateRef(stmt.cond) || containsStateRefStmts(stmt.body)
        is StmtAst.ReturnStmt -> stmt.value?.let(::containsStateRef) == true
        is StmtAst.TryStmt ->
            containsStateRefStmts(stmt.tryBody) ||
                containsStateRefStmts(stmt.catchBody) ||
                containsStateRefStmts(stmt.finallyBody)
        is StmtAst.ForInStmt -> containsStateRef(stmt.iterable) || containsStateRefStmts(stmt.body)
        is StmtAst.WhenStmt -> containsStateRef(stmt.whenExpr)
        is StmtAst.FunDeclStmt -> containsStateRefStmts(stmt.decl.body)
        else -> false
    }

    private fun containsStateRef(expr: ExprAst.WhenExpr): Boolean {
        if (containsStateRef(expr.subject)) return true
        for (branch in expr.branches) {
            if (containsStateRefs(branch.values) || containsStateRefStmts(branch.body)) return true
        }
        return expr.elseBody?.let { containsStateRefStmts(it) } == true
    }

    private fun containsStateRef(expr: ExprAst.IfExpr): Boolean =
        containsStateRef(expr.cond) || containsStateRefStmts(expr.thenBody) || containsStateRefStmts(expr.elseBody)

    /**
     * M6-b：完成元素体解析后，校验 source 属性与形参列表的强约束。
     *
     * - 仅 M6-b 容器白名单（column/row/box/scrollview）才把 props["source"] 视作反应式 source；
     *   inventory/slotGrid 等历史 kind 的 source 保留原语义，不参与 E700/E701 检查。
     * - 形参非空但无 source → E700；source 非空但形参为空 → E701。
     *
     * @return 与 [ElementAst.sourceExpr] 同步的表达式；非容器或未声明 source 时为 null。
     */
    private fun validateSourceBindings(
        kindTok: top.wcpe.allincore.dsl.compiler.lex.Token,
        props: List<ElementAst.PropAssign>,
        sourceParams: List<ElementAst.SourceParam>,
    ): top.wcpe.allincore.dsl.compiler.ast.ExprAst? {
        val isM6bContainer = kindTok.lexeme in M6B_CONTAINER_KINDS
        val sourceExpr = if (isM6bContainer) {
            props.firstOrNull { it.name == "source" }?.value
        } else null
        if (sourceParams.isNotEmpty() && sourceExpr == null) {
            p.fail(DslError.E700_CONTAINER_PARAMS_WITHOUT_SOURCE, kindTok.position, kindTok.lexeme)
        }
        if (sourceExpr != null && sourceParams.isEmpty()) {
            p.fail(DslError.E701_CONTAINER_SOURCE_WITHOUT_PARAMS, kindTok.position, kindTok.lexeme)
        }
        return sourceExpr
    }

    /**
     * 元素成员分隔符：NEWLINE 或 SEMICOLON 任一即可，二者可连续出现并混用。
     *
     * 支持单行多 prop（`a = 1; b = 2`）以及多行混用风格，与 vanilla 背包 DSL 保持一致。
     */
    private fun expectMemberSeparator() {
        if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
            p.fail("元素成员后缺少分隔符（NEWLINE 或 ';'），实际为 '${p.current().lexeme}'")
        }
        while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) {
            p.advance()
        }
    }

    /**
     * 解析 `on { trigger = "animName" ... }` 触发器块。
     *
     * 每条 `trigger = expr` 条目展开为一条 [ElementAst.PropAssign]，与直接写
     * `triggerName = expr` 等价；触发器名是否合法由后续语义阶段校验。
     *
     * M6-i 扩展：`trigger = "anim" -> ::funRef` 形态额外展开一条
     * `triggerComplete = ::funRef` PropAssign，由运行期把回调注入对应的
     * AnimationHandle.onComplete。回调必须是 `::fun` 或 `::fun(args)` 引用，
     * 否则报 [DslError.E760_TRIGGER_ON_COMPLETE_MUST_BE_FUN_REF]。
     */
    private fun parseOnBlock(props: MutableList<ElementAst.PropAssign>) {
        p.advance() // 消费 "on"
        p.advance() // 消费 "{"
        p.skipNewlines()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val triggerTok = p.expect(TokenType.IDENTIFIER, "on 块内期望触发器名")
            p.expect(TokenType.EQUALS, "on 块内触发器名后期望 '='")
            val expr = ExpressionParser(p).parse()
            props += ElementAst.PropAssign(
                name = triggerTok.lexeme,
                value = expr,
                position = triggerTok.position,
            )
            // M6-i：`trigger = "anim" -> ::funRef` 形态——再追加一条 onComplete PropAssign
            if (p.check(TokenType.ARROW)) {
                parseTriggerOnCompleteAndAppend(triggerTok, expr, props)
            }
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "on 块成员后缺少换行")
                p.skipNewlines()
            }
        }
        p.expect(TokenType.RBRACE, "on 块缺少 '}'")
    }

    /**
     * M6-i：消费 `->` 后解析 onComplete 回调表达式，并把它作为 `<trigger>Complete`
     * PropAssign 追加到 props 列表。回调必须是 `::fun` 或 `::fun(args)` 部分应用引用。
     */
    private fun parseTriggerOnCompleteAndAppend(
        triggerTok: top.wcpe.allincore.dsl.compiler.lex.Token,
        animExpr: top.wcpe.allincore.dsl.compiler.ast.ExprAst,
        props: MutableList<ElementAst.PropAssign>,
    ) {
        // 触发器名 = 字面量动画名是 RFC 推荐写法；非字面量也允许，由 runtime 兜底。
        // 仅对 onComplete 回调本身做形态校验（必须是 FunRefExpr）。
        p.advance() // 消费 ARROW
        val cbPos = p.current().position
        val callback = ExpressionParser(p).parse()
        if (callback !is top.wcpe.allincore.dsl.compiler.ast.ExprAst.FunRefExpr) {
            p.fail(
                DslError.E760_TRIGGER_ON_COMPLETE_MUST_BE_FUN_REF,
                cbPos,
                triggerTok.lexeme,
                callback::class.simpleName ?: "?",
            )
        }
        // 防御：动画名理应是 String 字面量；非字面量场景下 runtime 仍按表达式求值，
        // 这里只在编译期保留 PropAssign，不强制阻塞。
        @Suppress("UNUSED_VARIABLE") val _animExpr = animExpr
        props += ElementAst.PropAssign(
            name = triggerTok.lexeme + "Complete",
            value = callback,
            position = triggerTok.position,
        )
    }

    /**
     * 解析 `effects { kind { field = expr ... } ... }` / `modifiers { ... }` 装饰器块。
     *
     * 每个内部 `kind { ... }` 子块生成一个 [ModifierAst]，保留声明顺序。
     * 运行期由 IR v32 modifier 链还原为对应的视觉属性路由。
     */
    private fun parseEffectsBlock(blockName: String): List<ModifierAst> {
        p.advance() // 消费 "effects" / "modifiers"
        p.advance() // 消费 "{"
        p.skipNewlines()
        val modifiers = ModifierBundleParser(p).parseModifierItems()
        p.expect(TokenType.RBRACE, "$blockName 块缺少 '}'")
        return modifiers
    }

    private fun isStaticSymbolProp(name: String): Boolean =
        name == "variant" || name == "controlSize" || name == "textStyle"

    private fun parseStaticSymbolExpr(propName: String): ExprAst {
        if (p.check(TokenType.STRING)) {
            p.fail(DslError.AUI_V2_STATIC_SYMBOL_REQUIRED, p.current().position, propName)
        }
        if (!p.check(TokenType.KW_IF)) {
            return parseStaticSymbolLiteral(propName)
        }
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "if 后缺少 '('")
        val condition = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
        p.expect(TokenType.RPAREN, "if 条件缺少 ')'")
        p.skipNewlines()
        val thenExpr = parseStaticSymbolLiteral(propName)
        p.skipNewlines()
        p.expect(TokenType.KW_ELSE, "$propName 条件表达式缺少 else")
        p.skipNewlines()
        val elseExpr = parseStaticSymbolLiteral(propName)
        return ExprAst.IfExpr(
            condition,
            listOf(StmtAst.ExprStmt(thenExpr, thenExpr.position)),
            listOf(StmtAst.ExprStmt(elseExpr, elseExpr.position)),
            ifTok.position,
        )
    }

    private fun parseStaticSymbolLiteral(propName: String): ExprAst.Literal.StringLit {
        val tok = p.current()
        if (tok.type != TokenType.IDENTIFIER) {
            p.fail(DslError.AUI_V2_STATIC_SYMBOL_REQUIRED, tok.position, propName)
        }
        p.advance()
        if (p.check(TokenType.DOT) || p.check(TokenType.LPAREN)) {
            p.fail(DslError.AUI_V2_STATIC_SYMBOL_REQUIRED, tok.position, propName)
        }
        return ExprAst.Literal.StringLit(tok.lexeme, tok.position)
    }

    private fun isLegacyTextStyleProp(kindLexeme: String, name: String): Boolean =
        kindLexeme == "text" && (name == "size" || name == "color" || name == "font" || name == "typography")

    private fun isDomainBlockName(name: String): Boolean =
        name == "interaction" ||
            name == "events" ||
            name == "transitions" ||
            name == "animations" ||
            name == "textOverrides"

    private fun parseDomainBlock(name: String): List<ElementAst.PropAssign> {
        val head = p.advance()
        p.expect(TokenType.LBRACE, "$name 后缺少 '{'")
        p.skipNewlines()
        val props = when (name) {
            "interaction", "events" -> parseFlatDomainProps(prefix = "")
            "textOverrides" -> parseFlatDomainProps(prefix = "textOverrides.")
            "transitions" -> parseNestedDomainProps(prefix = "transitions")
            "animations" -> parseAnimationsDomainProps(head.position)
            else -> emptyList()
        }
        p.expect(TokenType.RBRACE, "$name 块缺少 '}'")
        return props
    }

    private fun parseFlatDomainProps(prefix: String): List<ElementAst.PropAssign> {
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val fieldTok = parseDomainFieldName("域块内缺少字段名")
            p.expect(TokenType.EQUALS, "域字段 '${fieldTok.lexeme}' 后缺少 '='")
            props += ElementAst.PropAssign(prefix + fieldTok.lexeme, parseDomainValue(fieldTok.lexeme), fieldTok.position)
            consumeDomainSeparator()
        }
        return props
    }

    private fun parseNestedDomainProps(prefix: String): List<ElementAst.PropAssign> {
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val nameTok = parseDomainFieldName("嵌套域缺少名称")
            p.expect(TokenType.LBRACE, "嵌套域 '${nameTok.lexeme}' 后缺少 '{'")
            p.skipNewlines()
            props += parseNestedDomainFields("$prefix.${nameTok.lexeme}")
            p.expect(TokenType.RBRACE, "嵌套域 '${nameTok.lexeme}' 缺少 '}'")
            consumeDomainSeparator()
        }
        return props
    }

    private fun parseNestedDomainFields(prefix: String): List<ElementAst.PropAssign> {
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val fieldTok = parseDomainFieldName("嵌套域字段缺少名称")
            if (p.check(TokenType.LBRACE)) {
                p.advance()
                p.skipNewlines()
                props += parseNestedDomainFields("$prefix.${fieldTok.lexeme}")
                p.expect(TokenType.RBRACE, "嵌套域 '${fieldTok.lexeme}' 缺少 '}'")
            } else {
                p.expect(TokenType.EQUALS, "嵌套域字段 '${fieldTok.lexeme}' 后缺少 '='")
                props += ElementAst.PropAssign("$prefix.${fieldTok.lexeme}", parseDomainValue(fieldTok.lexeme), fieldTok.position)
            }
            consumeDomainSeparator()
        }
        return props
    }

    private fun parseAnimationsDomainProps(position: SourcePosition): List<ElementAst.PropAssign> {
        val props = mutableListOf<ElementAst.PropAssign>()
        var index = 0
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val prefix = "animations.${index++}"
            if (p.check(TokenType.KW_IF)) {
                val ifTok = p.expect(TokenType.KW_IF)
                p.expect(TokenType.LPAREN, "animations.if 后缺少 '('")
                val condition = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
                p.expect(TokenType.RPAREN, "animations.if 条件缺少 ')'")
                p.skipNewlines()
                p.expect(TokenType.LBRACE, "animations.if 后缺少 '{'")
                p.skipNewlines()
                props += ElementAst.PropAssign("$prefix.condition", condition, ifTok.position)
                props += parseNestedDomainFields(prefix)
                p.expect(TokenType.RBRACE, "animations.if 块缺少 '}'")
            } else {
                props += parseNestedDomainProps(prefix)
            }
            consumeDomainSeparator()
        }
        if (index == 0) {
            return listOf(ElementAst.PropAssign("animations.empty", ExprAst.Literal.BoolLit(true, position), position))
        }
        return props
    }

    private fun parseDomainValue(propName: String): ExprAst {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER && isNextDomainSeparator(1)) {
            p.advance()
            return ExprAst.Literal.StringLit(cur.lexeme, cur.position)
        }
        return if (isStaticSymbolProp(propName)) {
            parseStaticSymbolExpr(propName)
        } else {
            ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
        }
    }

    private fun isNextDomainSeparator(offset: Int): Boolean =
        p.peek(offset).type == TokenType.NEWLINE ||
            p.peek(offset).type == TokenType.SEMICOLON ||
            p.peek(offset).type == TokenType.RBRACE ||
            p.peek(offset).type == TokenType.EOF

    private fun consumeDomainSeparator() {
        if (p.check(TokenType.RBRACE)) return
        if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
            p.fail("域字段后缺少分隔符（NEWLINE 或 ';'），实际为 '${p.current().lexeme}'")
        }
        while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) {
            p.advance()
        }
    }

    private fun parseDomainFieldName(message: String): top.wcpe.allincore.dsl.compiler.lex.Token {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER || cur.type == TokenType.KW_COLOR) {
            return p.advance()
        }
        p.fail("$message：期望 IDENTIFIER，实际为 ${cur.type}（'${cur.lexeme}'）", cur.position)
    }

    private fun isEffectBlockName(name: String): Boolean = name == "effects" || name == "modifiers"

    /**
     * v32：把旧视觉 prop / effect.* 兼容键提升为一等 modifier，避免样式字段进入普通 props。
     */
    private fun modifierFromStyleProp(prop: ElementAst.PropAssign): ModifierAst? {
        val effectPrefix = "effect."
        if (prop.name.startsWith(effectPrefix)) {
            val rest = prop.name.removePrefix(effectPrefix)
            val dot = rest.indexOf('.')
            if (dot <= 0 || dot >= rest.length - 1) {
                return null
            }
            return ModifierAst(
                kind = rest.substring(0, dot),
                fields = listOf(prop.copy(name = rest.substring(dot + 1))),
                position = prop.position,
            )
        }
        if (!isStyleModifierProp(prop.name)) {
            return null
        }
        return ModifierAst(
            kind = "style",
            fields = listOf(prop),
            position = prop.position,
        )
    }

    private fun isStyleModifierProp(name: String): Boolean =
        name == "background" ||
            name == "borderWidth" ||
            name == "borderColor" ||
            name == "cornerRadius" ||
            name == "cornerRadiusTL" ||
            name == "cornerRadiusTR" ||
            name == "cornerRadiusBR" ||
            name == "cornerRadiusBL" ||
            name == "gradientType" ||
            name == "gradientAngle" ||
            name == "gradientStops" ||
            name == "gradientStartColor" ||
            name == "gradientEndColor" ||
            name == "radialCenterX" ||
            name == "radialCenterY" ||
            name == "radialRadius" ||
            name == "shadowOffsetX" ||
            name == "shadowOffsetY" ||
            name == "shadowBlur" ||
            name == "shadowColor" ||
            name == "shadowSpread" ||
            name == "shadowInset" ||
            name == "boxShadows" ||
            name == "mask" ||
            name == "maskParam1" ||
            name == "maskParam2" ||
            name == "maskParam3" ||
            name == "mask.pie.startAngle" ||
            name == "mask.pie.sweepAngle"

    private fun warnLegacy(
        name: String,
        target: String,
        confidence: MigrationConfidence,
        position: SourcePosition,
    ) {
        p.warnMigration(
            DslError.AUI_V2_LEGACY_DEPRECATED_SYNTAX,
            position,
            MigrationHint(target, confidence),
            name,
            target,
        )
    }

    /**
     * 允许用作 `effects { kind { field = ... } }` 内字段名的 token 类型。
     * 除普通 IDENTIFIER 外，显式放行与字段语义重叠的关键字（如 `color`），
     * 避免关键字保留干扰自然书写。
     */
    private fun isEffectFieldName(type: TokenType): Boolean =
        type == TokenType.IDENTIFIER ||
            type == TokenType.KW_COLOR ||
            type == TokenType.KW_STRING ||
            type == TokenType.KW_NUMBER ||
            type == TokenType.KW_BOOL

    /**
     * 解析元素体内 `mask { type = "pie" startAngle = ... sweepAngle = ... param1/2/3 = ... }` 子块。
     *
     * 字段映射规则（参考 RFC §3.9.3 与 VisualPropContributor）：
     *  - `type`        → style modifier 字段 `mask`
     *  - `startAngle`  → style modifier 字段 `mask.pie.startAngle`
     *  - `sweepAngle`  → style modifier 字段 `mask.pie.sweepAngle`
     *  - `param1/2/3`  → style modifier 字段 `maskParam1/2/3`
     *
     * 块内字段间允许换行或 `;` 任一作分隔，行内可写 `mask { type="pie"; startAngle=0; sweepAngle=1 }`。
     *
     * 未识别字段会直接抛出 [ParseException]，避免拼写错误（如 `startAngles`）被静默
     * 当作普通 prop 落到元素属性上污染下游。错误信息明示 mask 块语境与未识别的字段名。
     */
    private fun parseMaskBlock(): ModifierAst {
        val maskTok = p.advance() // 消费 "mask"
        p.advance() // 消费 "{"
        p.skipNewlines()
        val fields = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val fieldTok = p.current()
            if (!isEffectFieldName(fieldTok.type)) {
                p.fail("mask 块内期望字段名，实际为 '${fieldTok.lexeme}'")
            }
            val propName = mapMaskFieldName(fieldTok.lexeme)
                ?: p.fail(
                    "mask 块内未识别字段 '${fieldTok.lexeme}'，仅支持 " +
                        "type/startAngle/sweepAngle/param1/param2/param3",
                    fieldTok.position,
                )
            p.advance()
            p.expect(TokenType.EQUALS, "mask.${fieldTok.lexeme} 后期望 '='")
            val expr = ExpressionParser(p).parse()
            fields += ElementAst.PropAssign(
                name = propName,
                value = expr,
                position = fieldTok.position,
            )
            if (!p.check(TokenType.RBRACE)) {
                if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
                    p.fail("mask 字段后缺少分隔符（NEWLINE 或 ';'），实际为 '${p.current().lexeme}'")
                }
                while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) {
                    p.advance()
                }
            }
        }
        val close = p.expect(TokenType.RBRACE, "mask 块缺少 '}'")
        return ModifierAst("style", fields, p.completePosition(maskTok.position, close))
    }

    /**
     * RFC-INPUT-EVENT-UNIFICATION §5.1 形式 2：解析事件 prop 块形式。
     *
     * 调用前提：当前 token 为事件 prop 名（IDENTIFIER），下一 token 为 `{`，
     * 且 [nameTok].lexeme 已经在 [EventProps.NAMES] 白名单中。
     *
     * 行为：消费事件名后，复用 [ActionBlockParser] 暴露的 ExpressionParser
     * 解析紧邻的独立 lambda 字面量；产出的 [ElementAst.PropAssign] 与等号
     * 形式 `onClick = { event -> ... }` 完全一致，下游零改动。形参数校验由
     * [top.wcpe.allincore.dsl.compiler.semantic.ReferenceResolver] 在语义阶段
     * 统一负责（DSL_E301）。
     */
    private fun parseEventPropBlockForm(
        nameTok: top.wcpe.allincore.dsl.compiler.lex.Token,
        props: MutableList<ElementAst.PropAssign>,
    ) {
        p.advance() // 消费事件 prop 名
        val exprParser = ActionBlockParser(p).expressionParser(allowBareIdent = false)
        val lambda = exprParser.parseStandaloneLambda()
        props += ElementAst.PropAssign(
            name = nameTok.lexeme,
            value = lambda,
            position = nameTok.position,
        )
    }

    /**
     * 把 mask 块内的字段名映射为最终 PropAssign 的 prop 名。
     *
     * 仅接受白名单字段；返回 null 表示字段未识别，调用方应据此报错。
     */
    private fun mapMaskFieldName(field: String): String? = when (field) {
        "type" -> "mask"
        "startAngle" -> "mask.pie.startAngle"
        "sweepAngle" -> "mask.pie.sweepAngle"
        "param1" -> "maskParam1"
        "param2" -> "maskParam2"
        "param3" -> "maskParam3"
        else -> null
    }

    private fun isMemberHead(type: TokenType): Boolean =
        type == TokenType.IDENTIFIER ||
            type == TokenType.KW_ANIMATION ||
            type == TokenType.KW_TRANSITION ||
            type == TokenType.KW_COMPONENT ||
            type == TokenType.KW_PROPS ||
            type == TokenType.KW_STRING ||
            type == TokenType.KW_NUMBER ||
            type == TokenType.KW_BOOL ||
            type == TokenType.KW_COLOR ||
            type == TokenType.KW_FUN ||
            type == TokenType.KW_CANVAS

    /**
     * 把 `canvas "id" { ... }` 解析为 [ElementAst]（kind=canvas，children 强制空）。
     *
     * 解析真正交给 [CanvasBlockParser]；ElementAst 仅做外壳包装，以便下游
     * [top.wcpe.allincore.dsl.compiler.codegen.ElementEmitter] 统一产出 UiNode。
     */
    private fun parseCanvasElement(): ElementAst {
        val canvas: CanvasAst = CanvasBlockParser(p).parse()
        return ElementAst(
            kind = "canvas",
            id = canvas.id,
            props = canvas.attrs,
            children = emptyList(),
            canvasData = canvas,
            position = canvas.position,
        )
    }

    /**
     * Phase B.4：在解析容器元素体期间为 source 形参开启块作用域。
     *
     * 把每个非丢弃形参（`_` 除外）通过 [Parser.declareSourceParam] 注册到独立于
     * `var`-类局部的 source 形参栈，使 `item = entry` / `key = k` 这类裸标识符
     * 在表达式中能被升格为 [top.wcpe.allincore.dsl.compiler.ast.ExprAst.IdentRef]
     * 而非误报"标识符后缺少 '('"。
     *
     * 选用 IdentRef 而非 LocalRef 的原因：source 形参在运行时由
     * [top.wcpe.allincore.ui.runtime.ir.IrInterpreter.instantiateTemplate] 写入
     * `EvalContext.locals`，恰好对应 IdentRef 分支的解析路径；若升格为 LocalRef
     * 会触发 `未声明的局部变量` 异常。
     *
     * 无论 [body] 正常返回还是抛出，作用域都会被弹出，与
     * [ActionBlockParser.withScopedBlock] 的资源管理风格保持一致。
     */
    private inline fun <T> withSourceParamScope(
        sourceParams: List<ElementAst.SourceParam>,
        implicitParams: List<String> = emptyList(),
        body: () -> T,
    ): T {
        p.pushSourceParamScope()
        try {
            for (param in sourceParams) {
                if (param.name == "_") continue
                p.declareSourceParam(param.name)
            }
            for (name in implicitParams) {
                p.declareSourceParam(name)
            }
            return body()
        } finally {
            p.popSourceParamScope()
        }
    }

    /**
     * M6-b：探测并解析容器形参列表 `{ item -> ... }` / `{ k, v -> ... }`。
     *
     * 调用前提：`{` 已被消费，已 skipNewlines。
     *
     * 探测规则：未越过同级 `{` / `}` / `=` 之前是否能遇到 `->`；
     * 不是形参列表时返回空 List 并保持游标原位（让后续 props/children 走原解析路径）。
     *
     * 形参数 >= 3 报 E703（在解析期直接抛错，附 0 形态信息）。
     */
    private fun tryParseSourceParams(kindLexeme: String): List<ElementAst.SourceParam> {
        if (!looksLikeSourceParams()) return emptyList()
        val params = mutableListOf<ElementAst.SourceParam>()
        params += parseOneSourceParam()
        while (p.check(TokenType.COMMA)) {
            p.advance()
            p.skipNewlines()
            params += parseOneSourceParam()
        }
        if (params.size >= 3) {
            p.fail(
                DslError.E703_CONTAINER_PARAM_COUNT_MISMATCH,
                params.first().position,
                kindLexeme, params.size, "形参数必须为 1（List）或 2（Map）",
            )
        }
        p.expect(TokenType.ARROW, "容器形参列表缺少 '->'")
        p.skipNewlines()
        return params
    }

    /** 解析单个容器形参：IDENT (: Type)?。 */
    private fun parseOneSourceParam(): ElementAst.SourceParam {
        val nameTok = p.expect(TokenType.IDENTIFIER, "容器形参名缺失")
        val type = if (p.check(TokenType.COLON)) {
            p.advance()
            TypeParser.parse(p)
        } else null
        return ElementAst.SourceParam(nameTok.lexeme, type, nameTok.position)
    }

    /**
     * 前瞻判断：当前 token 起到下一个 `->` 之前，是否仅由形参合法 token 构成。
     *
     * 形参合法 token：IDENTIFIER / COMMA / NEWLINE / COLON / 类型相关 token
     * （IDENT、`<`、`>`、`?`、KW_STRING/KW_NUMBER/KW_BOOL/KW_COLOR、`(`、`)`、ARROW within type）。
     *
     * 为了避免类型函数体内的 ARROW 混淆，本前瞻只关心：
     *  - 首 token 必须是 IDENTIFIER（形参名起始）
     *  - 在遇到 `=` / `{` / `}` / EOF 等"非形参"token 时立即放弃
     *  - 在遇到顶层 `->` 时确认是形参列表
     *
     * 简化处理：扫描时跟踪 `<...>` 与 `(...)` 深度。
     */
    private fun looksLikeSourceParams(): Boolean {
        if (!p.check(TokenType.IDENTIFIER)) return false
        var k = 0
        var angle = 0
        var paren = 0
        while (true) {
            val t = p.peek(k).type
            when (t) {
                TokenType.ARROW -> if (angle == 0 && paren == 0) return true
                TokenType.LT -> angle++
                TokenType.GT -> if (angle > 0) angle--
                TokenType.LPAREN -> paren++
                TokenType.RPAREN -> if (paren > 0) paren--
                TokenType.EQUALS, TokenType.LBRACE, TokenType.RBRACE,
                TokenType.SEMICOLON, TokenType.EOF, TokenType.STRING,
                TokenType.NUMBER, TokenType.BOOLEAN, -> return false
                else -> Unit
            }
            k++
            if (k > 64) return false
        }
    }

    // ========================================================================
    // RFC-RECOMPOSITION：元素树位置的 if/when 条件分支解析
    // ========================================================================

    /**
     * 解析元素树位置的 conditional 子元素：识别 `if (cond) { ... } else if/else ...`
     * 与 `when (subject) { v -> ... else -> ... }` 两种语法形态，统一返回一个
     * kind=$cond 的 ElementAst 包装节点（详见 [ConditionalContentAst]）。
     *
     * @param allowOverlayPseudoElements 透传给 branch body 内的 [parseElement]，与外层一致。
     */
    private fun parseConditionalElement(allowOverlayPseudoElements: Boolean): ElementAst {
        val startTok = p.current()
        return if (startTok.type == TokenType.KW_IF) {
            parseIfElementChain(allowOverlayPseudoElements)
        } else {
            parseWhenElementBlock(allowOverlayPseudoElements)
        }
    }

    /** 解析 if / else if / else 链，构造 ConditionalContentAst（subject = null）。 */
    private fun parseIfElementChain(allowOverlayPseudoElements: Boolean): ElementAst {
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "if 后缺少 '('")
        val cond = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
        p.expect(TokenType.RPAREN, "if 条件缺少 ')'")
        p.skipNewlines()
        val thenBody = parseConditionalBranchBody(allowOverlayPseudoElements, ifTok.position)

        val branches = mutableListOf(
            ConditionalContentAst.Branch(
                condition = cond,
                values = emptyList(),
                body = thenBody,
                position = ifTok.position,
            )
        )

        var elseBranch: ElementAst? = null
        // 跨越 NEWLINE 偷看 else / else if（与 ActionBlockParser.parseIf 同语法约定）
        while (true) {
            var k = 0
            while (p.peek(k).type == TokenType.NEWLINE) k++
            if (p.peek(k).type != TokenType.KW_ELSE) break
            p.skipNewlines()
            p.advance() // KW_ELSE
            if (p.check(TokenType.KW_IF)) {
                val nestedTok = p.advance() // KW_IF
                p.expect(TokenType.LPAREN, "else if 后缺少 '('")
                val nestedCond = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
                p.expect(TokenType.RPAREN, "else if 条件缺少 ')'")
                p.skipNewlines()
                val nestedBody = parseConditionalBranchBody(allowOverlayPseudoElements, nestedTok.position)
                branches += ConditionalContentAst.Branch(
                    condition = nestedCond,
                    values = emptyList(),
                    body = nestedBody,
                    position = nestedTok.position,
                )
            } else {
                p.skipNewlines()
                elseBranch = parseConditionalBranchBody(allowOverlayPseudoElements, ifTok.position)
                break
            }
        }

        val position = p.completePosition(ifTok.position)
        val content = ConditionalContentAst(
            subject = null,
            branches = branches,
            elseBranch = elseBranch,
            position = position,
        )
        return wrapConditional(content, position)
    }

    /**
     * 解析 when 块，构造 ConditionalContentAst：
     * - `when (subject) { v1 -> ..., v2 -> ..., else -> ... }`：值匹配形态，subject 非 null
     * - `when { boolExpr -> ..., else -> ... }`：无 subject 多布尔分支形态（语法糖，等价于
     *    if/else if/else 链），subject = null，每个 branch 的 condition 是任意布尔表达式
     */
    private fun parseWhenElementBlock(allowOverlayPseudoElements: Boolean): ElementAst {
        val whenTok = p.expect(TokenType.KW_WHEN)
        // 形态分流：直接 LBRACE 表示无 subject 形态
        val hasSubject = p.check(TokenType.LPAREN)
        val subject: ExprAst? = if (hasSubject) {
            p.advance() // LPAREN
            val s = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
            p.expect(TokenType.RPAREN, "when 主体缺少 ')'")
            p.skipNewlines()
            s
        } else {
            null
        }
        p.expect(TokenType.LBRACE, "when 缺少 '{'")
        p.skipNewlines()

        val branches = mutableListOf<ConditionalContentAst.Branch>()
        var elseBranch: ElementAst? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            if (p.check(TokenType.KW_ELSE)) {
                if (elseBranch != null) {
                    p.fail("when 仅允许一个 else 分支", p.current().position)
                }
                p.advance()
                p.expect(TokenType.ARROW, "when else 分支缺少 '->'")
                p.skipNewlines()
                elseBranch = parseConditionalBranchBody(allowOverlayPseudoElements, whenTok.position)
                p.skipNewlines()
                continue
            }
            val branchPos = p.current().position
            val branch: ConditionalContentAst.Branch = if (hasSubject) {
                // 值匹配形态：v1[, v2, ...] -> body；values 仅接受字面量
                val values = mutableListOf<ExprAst>()
                values += parseConditionalWhenValue()
                while (p.check(TokenType.COMMA)) {
                    p.advance()
                    p.skipNewlines()
                    values += parseConditionalWhenValue()
                }
                p.expect(TokenType.ARROW, "when 分支缺少 '->'")
                p.skipNewlines()
                val body = parseConditionalBranchBody(allowOverlayPseudoElements, branchPos)
                ConditionalContentAst.Branch(
                    condition = null,
                    values = values,
                    body = body,
                    position = branchPos,
                )
            } else {
                // 无 subject 形态：boolExpr -> body；condition 是任意表达式（与 if 形态等价）
                val cond = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
                p.expect(TokenType.ARROW, "when 分支缺少 '->'")
                p.skipNewlines()
                val body = parseConditionalBranchBody(allowOverlayPseudoElements, branchPos)
                ConditionalContentAst.Branch(
                    condition = cond,
                    values = emptyList(),
                    body = body,
                    position = branchPos,
                )
            }
            branches += branch
            p.skipNewlines()
        }
        val close = p.expect(TokenType.RBRACE, "when 缺少 '}'")
        val position = p.completePosition(whenTok.position, close)

        val content = ConditionalContentAst(
            subject = subject,
            branches = branches,
            elseBranch = elseBranch,
            position = position,
        )
        return wrapConditional(content, position)
    }

    /**
     * 解析 when 分支的单个值表达式，限制为字面量或 null（与 [ActionBlockParser.parseWhenValue] 同约束）。
     */
    private fun parseConditionalWhenValue(): ExprAst {
        val expr = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
        if (expr !is ExprAst.Literal && expr !is ExprAst.NullLit) {
            p.fail(DslError.E843_CONDITIONAL_BRANCH_VALUE_NOT_LITERAL, expr.position)
        }
        return expr
    }

    /**
     * 解析 conditional 分支体：必须是单根 element。
     *
     * 接受两种写法：
     * - `branch -> column "x" { ... }`（直接 element）
     * - `branch -> { column "x" { ... } }`（包一层花括号）
     *
     * 多根或空体直接报 E841。
     */
    private fun parseConditionalBranchBody(
        allowOverlayPseudoElements: Boolean,
        position: SourcePosition,
        multiRootError: DslError = DslError.E841_CONDITIONAL_BRANCH_MULTI_ROOT,
    ): ElementAst {
        if (p.check(TokenType.LBRACE)) {
            p.advance()
            p.skipNewlines()
            if (p.check(TokenType.RBRACE)) {
                p.fail(multiRootError, position)
            }
            val root = parseElement(allowOverlayPseudoElements)
            p.skipNewlines()
            if (!p.check(TokenType.RBRACE)) {
                p.fail(multiRootError, position)
            }
            p.expect(TokenType.RBRACE, "branch body 缺少 '}'")
            return root
        }
        return parseElement(allowOverlayPseudoElements)
    }

    /**
     * 把 ConditionalContentAst 包装为一个 kind="$cond" 的 ElementAst，便于复用现有 element children 容器。
     *
     * 包装节点 children 永远为空；实际分支元素由 conditionalContent.branches/elseBranch 携带。
     * id 自动生成为 `"$cond_<line>_<column>"`，不会与用户自定义 id 冲突（用户 id 必须是合法 IDENTIFIER）。
     */
    private fun wrapConditional(content: ConditionalContentAst, position: SourcePosition): ElementAst {
        val id = "\$cond_${position.line}_${position.column}"
        return ElementAst(
            kind = ConditionalContentAst.KIND_CONDITIONAL,
            id = id,
            props = emptyList(),
            children = emptyList(),
            conditionalContent = content,
            position = position,
        )
    }

    // ========================================================================
    // $for：元素树位置的 for 循环重组解析
    // ========================================================================

    /**
     * 解析元素树位置的 for 循环子元素，识别三种语法形态：
     * - `for (item in xs) { ... }`：单变量；
     * - `for (item, index in xs) { ... }`：额外暴露下标 index；
     * - `for ((k, v) in map) { ... }`：Map 双变量解构。
     *
     * 形态无歧义：`for (` 之后若紧跟 `(` ⇒ Map 解构；否则解析首个标识符，其后若是 `,` ⇒ 第二个是 index。
     *
     * 统一返回一个 kind=$for 的 ElementAst 包装节点（详见 [ForContentAst]）。
     */
    private fun parseForElement(allowOverlayPseudoElements: Boolean): ElementAst {
        val forTok = p.expect(TokenType.KW_FOR)
        p.expect(TokenType.LPAREN, "for 后缺少 '('")

        val var1: String
        var var2: String? = null
        val overMap: Boolean
        if (p.check(TokenType.LPAREN)) {
            // Map 解构形态：for ((k, v) in map)
            p.advance() // 内层 '('
            var1 = p.expect(TokenType.IDENTIFIER, "for Map 解构缺少 key 变量名").lexeme
            p.expect(TokenType.COMMA, "for Map 解构 key/value 之间缺少 ','")
            var2 = p.expect(TokenType.IDENTIFIER, "for Map 解构缺少 value 变量名").lexeme
            p.expect(TokenType.RPAREN, "for Map 解构缺少 ')'")
            overMap = true
        } else {
            var1 = p.expect(TokenType.IDENTIFIER, "for 缺少循环变量名").lexeme
            if (p.check(TokenType.COMMA)) {
                p.advance()
                var2 = p.expect(TokenType.IDENTIFIER, "for 缺少下标变量名").lexeme
            }
            overMap = false
        }

        p.expect(TokenType.KW_IN, "for 缺少 'in'")
        val iterable = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()

        // 可选 `; key = <expr>` 子句——用于显式指定每个 item 的 diff key
        var keyExpr: ExprAst? = null
        if (p.check(TokenType.SEMICOLON)) {
            p.advance()
            val keyIdTok = p.expect(TokenType.IDENTIFIER, "for ';' 后缺少上下文关键字 key")
            if (keyIdTok.lexeme != "key") {
                p.fail("for ';' 子句只支持 key = <expr>，实际：'${keyIdTok.lexeme}'")
            }
            p.expect(TokenType.EQUALS, "for key 子句缺少 '='")
            // 允许裸标识符：循环变量本身常用作 key（如 `key = item`、基本类型列表 `key = s`），
            // 运行期经 EvalContext.locals 解析为循环变量值，与体内引用同一机制。
            keyExpr = ActionBlockParser(p).expressionParser(allowBareIdent = true).parse()
        }

        p.expect(TokenType.RPAREN, "for 头部缺少 ')'")
        p.skipNewlines()

        // 把循环变量注册到 source 形参作用域，使其在体内可作为裸标识符（IdentRef）被引用，
        // 与 `{ item -> }` 容器形参同一机制（运行时通过 EvalContext.locals 注入）。
        val scopeParams = buildList {
            if (var1 != "_") add(ElementAst.SourceParam(var1, null, forTok.position))
            val v2 = var2
            if (v2 != null && v2 != "_") add(ElementAst.SourceParam(v2, null, forTok.position))
        }
        val body = withSourceParamScope(scopeParams) {
            parseConditionalBranchBody(
                allowOverlayPseudoElements,
                forTok.position,
                DslError.E845_FOR_BODY_MULTI_ROOT,
            )
        }

        val position = p.completePosition(forTok.position)
        val content = ForContentAst(
            iterable = iterable,
            var1 = var1,
            var2 = var2,
            overMap = overMap,
            body = body,
            position = position,
            keyExpr = keyExpr,
        )
        return wrapFor(content, position)
    }

    /**
     * 把 ForContentAst 包装为一个 kind="$for" 的 ElementAst，复用现有 element children 容器。
     *
     * 包装节点 children 永远为空；实际循环体由 forContent.body 携带。
     * id 自动生成为 `"$for_<line>_<column>"`，不会与用户自定义 id 冲突。
     */
    private fun wrapFor(content: ForContentAst, position: SourcePosition): ElementAst {
        val id = "\$for_${position.line}_${position.column}"
        return ElementAst(
            kind = ForContentAst.KIND_FOR,
            id = id,
            props = emptyList(),
            children = emptyList(),
            forContent = content,
            position = position,
        )
    }
}
